import { RiQuestionLine, RiStarFill, RiStarLine } from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";

/**
 * Монета отмечена избранным целиком §6.6 — то же различение «неизвестно» /
 * «известно», что и у заметки: список избранного может быть ещё не прочитан
 * или недоступен, и звёздочка не вправе в этом случае утверждать «не в
 * избранном» (спека `user-favorites`, `web-density-table`).
 */
export type FavoriteIndicator = "yes" | "no" | "unknown";

/**
 * Подпись звёздочки — общая для `aria-label` и `title`, когда состояние
 * неизвестно (review stage6-favorites, второй круг, п. 2): непрочитанный
 * список отличим от «не в избранном» иконкой, а не только цветом, и подпись
 * называет, что сделает нажатие, — оно перечитывает список, а не пишет.
 */
function favoriteLabel(match: FavoriteIndicator): string {
  if (match === "unknown") {
    return "Избранное не прочитано — нажмите, чтобы перечитать";
  }
  return match === "yes" ? "Убрать из избранного" : "Добавить в избранное";
}

export interface FavoriteStarButtonProps {
  /** Избранное монеты — состояние сопоставления клиента, не поле снимка. */
  match: FavoriteIndicator;
  /** Свой запрос избранного этой монеты ещё не завершился. */
  pending: boolean;
  /** Поставить или снять избранное монеты целиком. */
  onToggle: () => void;
  /**
   * Строка исключает кнопку из роверного `tabindex`: доступ с клавиатуры —
   * хоткеем `f` сфокусированной строки, а не вторым проходом табом (review
   * stage5-notes-and-pin, п. 1). Панель такого не делает — там кнопка входит
   * в обычный табовый проход.
   */
  tabIndex?: number;
  /**
   * Строка гасит активацию кнопки мышью/`Enter`/`Space`, чтобы клик по
   * звёздочке не открывал панель заодно (`stopButtonActivation` в
   * `density-row.tsx`). Панель передаёт действие напрямую.
   */
  stopActivation?: (event: ReactMouseEvent | ReactKeyboardEvent) => void;
  /** Довесок к позиционированию кнопки в разметке вызывающей стороны. */
  className?: string;
}

/**
 * Звёздочка избранного §6.6 — один компонент экрана для строки таблицы и
 * заголовка детальной панели (review stage6-favorites, п. 8): дублирование
 * разметки между ними — будущее расхождение стилей, а не экономия времени.
 * Домена в компоненте нет, но и в UIKit ему не место — он завязан на понятие
 * избранного и сопоставление клиента, которых UIKit не знает.
 */
export function FavoriteStarButton({
  match,
  pending,
  onToggle,
  tabIndex,
  stopActivation,
  className,
}: FavoriteStarButtonProps) {
  const label = favoriteLabel(match);
  // Непрочитанный список не делает кнопку недоступной (review
  // stage6-favorites, второй круг, п. 1 и 2): нажатие на неё перечитывает
  // список, а не пишет. Недоступна кнопка только пока идёт собственное
  // действие монеты (`pending`) — и различить эти два случая обязана иконка,
  // а не класс: вопрос вместо звезды, не «та же звезда, но приглушённая».
  const unknown = match === "unknown";

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            tabIndex={tabIndex}
            disabled={pending}
            aria-pressed={unknown ? "mixed" : match === "yes"}
            aria-label={label}
            className={cn(
              "size-6",
              match === "yes" ? "text-foreground" : "text-muted-foreground",
              className,
            )}
            onClick={(event) => {
              stopActivation?.(event);
              onToggle();
            }}
            onKeyDown={stopActivation}
          />
        }
      >
        {unknown ? (
          <RiQuestionLine className="size-3.5" />
        ) : match === "yes" ? (
          <RiStarFill className="size-3.5" />
        ) : (
          <RiStarLine className="size-3.5" />
        )}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
